import {
  AppBar,
  Box,
  Divider,
  IconButton,
  Typography,
  useColorScheme,
  Button,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import logo from '../../assets/nav_logo.png';
import { navColorStyles } from '../../../vendor/mui-devias-kit/components/dashboard/layout/vertical/styles';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { useSettings } from '../../../vendor/mui-devias-kit/components/core/settings/settings-context';
import {
  Book,
  Buildings,
  ListChecks,
  Stethoscope,
  ThumbsUp,
  UserCircle,
  UserList,
  ArrowsClockwise,
  ChatCircle,
  CalendarBlank,
  List,
  X,
} from '@phosphor-icons/react';
import { usePopover } from '../../../vendor/mui-devias-kit/hooks/use-popover';
import UserPopover from './UserPopover';
import { stormGrey } from '../../../vendor/mui-devias-kit/styles/theme/colors';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { useUserStatusContext } from '../../contexts';
import { Api } from '../../api';
import { useAuthRoles } from '../../hooks';

interface NavbarProps {
  viewMode: 'admin' | 'clinician';
}

export default function Navbar({ viewMode }: NavbarProps) {
  const { myClinicianStatus } = useUserStatusContext();
  const { settings } = useSettings();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const popover = usePopover<HTMLButtonElement>();
  const { colorScheme = 'light' } = useColorScheme();

  const {
    admin: isAdmin,
    coach: isCoach,
    physician: isPhysician,
  } = useAuthRoles();
  const hasAccess = isAdmin || isCoach || isPhysician;
  const isAdminAndClinician = isAdmin && (isCoach || isPhysician);

  const navColor: keyof (typeof navColorStyles)['light'] =
    settings.dashboardNavColor ?? 'evident';

  const styles = navColorStyles[colorScheme][navColor];

  return (
    <Box sx={{ ...styles }}>
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{
          boxShadow: 'none',
          bgcolor: 'var(--SideNav-background)',
          borderBottom: `1px solid ${stormGrey[700]}`,
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          height={69}
        >
          <Box display="flex" alignItems="center">
            <img src={logo} alt="Sustain Logo" width={115} height={37} />
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && isAdminAndClinician && (
              <Button
                variant="outlined"
                startIcon={<ArrowsClockwise size={20} color="#667085" />}
                onClick={() => {
                  dispatch({
                    type: 'SET_VIEW_MODE',
                    payload: {
                      viewMode: viewMode === 'admin' ? 'clinician' : 'admin',
                    },
                  });
                  navigate(viewMode === 'admin' ? '/users' : '/library');
                }}
                sx={{
                  color: 'white',
                  borderColor: '#203647',
                  '&:hover': {
                    borderColor: '#203647',
                  },
                }}
              >
                Change view to {viewMode === 'admin' ? 'clinician' : 'admin'}
              </Button>
            )}
            {isMobile ? (
              <IconButton onClick={() => setMobileDrawerOpen(true)}>
                <List size={24} color="#667085" />
              </IconButton>
            ) : (
              <>
                <IconButton
                  ref={popover.anchorRef}
                  onClick={popover.handleToggle}
                >
                  <UserCircle size={24} color="#667085" />
                </IconButton>
                <UserPopover
                  anchorEl={popover.anchorRef.current}
                  onClose={popover.handleClose}
                  open={popover.open}
                />
              </>
            )}
          </Box>
        </Box>
        <Divider sx={{ borderColor: '#434A60' }} />
        {!isMobile && hasAccess && (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            px={2}
            py={1}
            gap={2}
          >
            {viewMode === 'admin' ? (
              <>
                <NavItem label="Library Content" Icon={Book} href="/library" />
                <NavItem
                  label="Program Content"
                  Icon={ListChecks}
                  href="/program"
                />
                <NavItem
                  label="Clinicians"
                  Icon={Stethoscope}
                  href="/clinicians"
                />
                <NavItem label="App Users" Icon={UserList} href="/users" />
                <NavItem label="Feedback" Icon={ThumbsUp} href="/feedback" />
                <NavItem label="B2B" Icon={Buildings} href="/b2b" />
              </>
            ) : (
              <>
                <NavItem
                  label="Your Clients"
                  Icon={UserList}
                  href="/users"
                  showIndicator={myClinicianStatus.hasNewClients}
                />
                <NavItem
                  label="Chat"
                  Icon={ChatCircle}
                  href="/chat"
                  showIndicator={myClinicianStatus.hasUnreadMessages}
                />
                <NavItem
                  label="Appointments"
                  Icon={CalendarBlank}
                  href="/appointments"
                  showIndicator={myClinicianStatus.hasAppointmentsToday}
                />
                {isCoach && (
                  <NavItem
                    label="B2B Teams"
                    Icon={Buildings}
                    href="/b2b-teams"
                  />
                )}
              </>
            )}
          </Box>
        )}
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            backgroundColor: '#121621',
            color: '#F1F1F4',
            width: 280,
          },
        }}
      >
        <MobileDrawerContent
          viewMode={viewMode}
          isAdminAndClinician={isAdminAndClinician}
          isCoach={isCoach}
          dispatch={dispatch}
          navigate={navigate}
          onClose={() => setMobileDrawerOpen(false)}
          popover={popover}
          myClinicianStatus={myClinicianStatus}
        />
      </Drawer>
    </Box>
  );
}

function RedDotIndicator() {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: -4,
        right: -4,
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: '#F04438',
      }}
    />
  );
}

function NavItem({
  label,
  Icon,
  href,
  disabled = false,
  showIndicator = false,
}: {
  label: string;
  Icon: typeof Book;
  href: string;
  disabled?: boolean;
  showIndicator?: boolean;
}) {
  const pathname = useLocation().pathname;
  const active = pathname.split('/')[1] === href.split('/')[1];

  return (
    <Button
      component={NavLink}
      to={href}
      disabled={disabled}
      variant="text"
      color={active ? 'primary' : 'secondary'}
      sx={{
        alignItems: 'center',
        borderRadius: 1,
        display: 'flex',
        flex: '0 0 auto',
        gap: 1,
        p: '6px 16px',
        textTransform: 'none',
        fontWeight: 500,
        minWidth: 0,
        boxShadow: 'none',
        bgcolor: active ? '#2A4F65' : 'transparent',
        '&:hover': {
          bgcolor: active ? '#2A4F65' : 'transparent',
        },
      }}
      tabIndex={0}
    >
      {Icon ? (
        <Box sx={{ position: 'relative', display: 'flex', marginRight: '8px' }}>
          <Icon size={22} color={active ? '#fff' : '#8A94A6'} />
          {showIndicator && <RedDotIndicator />}
        </Box>
      ) : null}
      <Typography
        component="span"
        sx={{
          color: active ? '#fff' : '#F1F1F4',
          fontSize: '0.875rem',
          fontWeight: 500,
          lineHeight: '28px',
        }}
      >
        {label}
      </Typography>
    </Button>
  );
}

function MobileDrawerContent({
  viewMode,
  isAdminAndClinician,
  isCoach,
  dispatch,
  navigate,
  onClose,
  popover,
  myClinicianStatus,
}: {
  viewMode: 'admin' | 'clinician';
  isAdminAndClinician: boolean;
  isCoach: boolean;
  dispatch: any;
  navigate: any;
  onClose: () => void;
  popover: any;
  myClinicianStatus: Api.MyClinicianStatus.V;
}) {
  const handleNavigation = (href: string) => {
    navigate(href);
    onClose();
  };

  const handleViewModeChange = () => {
    dispatch({
      type: 'SET_VIEW_MODE',
      payload: {
        viewMode: viewMode === 'admin' ? 'clinician' : 'admin',
      },
    });
    navigate(viewMode === 'admin' ? '/users' : '/library');
    onClose();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          p: 2,
          borderBottom: '1px solid #434A60',
        }}
      >
        <IconButton onClick={onClose}>
          <X size={24} color="#667085" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, p: 2 }}>
        <Box sx={{ mb: 3 }}>
          {viewMode === 'admin' ? (
            <>
              <MobileNavItem
                label="Library Content"
                Icon={Book}
                onClick={() => handleNavigation('/library')}
                href="/library"
              />
              <MobileNavItem
                label="Program Content"
                Icon={ListChecks}
                onClick={() => handleNavigation('/program')}
                href="/program"
              />
              <MobileNavItem
                label="Clinicians"
                Icon={Stethoscope}
                onClick={() => handleNavigation('/clinicians')}
                href="/clinicians"
              />
              <MobileNavItem
                label="App Users"
                Icon={UserList}
                onClick={() => handleNavigation('/users')}
                href="/users"
              />
              <MobileNavItem
                label="Feedback"
                Icon={ThumbsUp}
                onClick={() => handleNavigation('/feedback')}
                href="/feedback"
              />
              <MobileNavItem
                label="B2B"
                Icon={Buildings}
                onClick={() => handleNavigation('/b2b')}
                href="/b2b"
              />
            </>
          ) : (
            <>
              <MobileNavItem
                label="Your Clients"
                Icon={UserList}
                onClick={() => handleNavigation('/users')}
                href="/users"
                showIndicator={myClinicianStatus.hasNewClients}
              />
              <MobileNavItem
                label="Chat"
                Icon={ChatCircle}
                onClick={() => handleNavigation('/chat')}
                href="/chat"
                showIndicator={myClinicianStatus.hasUnreadMessages}
              />
              <MobileNavItem
                label="Appointments"
                Icon={CalendarBlank}
                onClick={() => handleNavigation('/appointments')}
                href="/appointments"
                showIndicator={myClinicianStatus.hasAppointmentsToday}
              />
              {isCoach && (
                <MobileNavItem
                  label="B2B Teams"
                  Icon={Buildings}
                  onClick={() => handleNavigation('/b2b-teams')}
                  href="/b2b-teams"
                />
              )}
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid #434A60' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isAdminAndClinician && (
            <Button
              variant="outlined"
              startIcon={<ArrowsClockwise size={20} color="#667085" />}
              onClick={handleViewModeChange}
              sx={{
                color: 'white',
                borderColor: '#203647',
                '&:hover': {
                  borderColor: '#203647',
                },
              }}
            >
              Change view to {viewMode === 'admin' ? 'clinician' : 'admin'}
            </Button>
          )}
          <IconButton ref={popover.anchorRef} onClick={popover.handleToggle}>
            <UserCircle size={24} color="#667085" />
          </IconButton>
          <UserPopover
            anchorEl={popover.anchorRef.current}
            onClose={popover.handleClose}
            open={popover.open}
          />
        </Box>
      </Box>
    </Box>
  );
}

function MobileNavItem({
  label,
  Icon,
  onClick,
  href,
  showIndicator = false,
}: {
  label: string;
  Icon: typeof Book;
  onClick: () => void;
  href: string;
  showIndicator?: boolean;
}) {
  const pathname = useLocation().pathname;
  const active = pathname.split('/')[1] === href.split('/')[1];

  return (
    <Button
      fullWidth
      variant="text"
      onClick={onClick}
      sx={{
        color: 'white',
        justifyContent: 'flex-start',
        mb: 1,
        py: 1.5,
        backgroundColor: active ? '#2E323C' : 'transparent',
        '&:hover': {
          backgroundColor: active ? '#2E323C' : 'rgba(255, 255, 255, 0.05)',
        },
      }}
    >
      <Box sx={{ position: 'relative', display: 'flex', marginRight: '12px' }}>
        <Icon size={20} color="#8A94A6" />
        {showIndicator && <RedDotIndicator />}
      </Box>
      <Typography variant="body1" sx={{ fontWeight: 400, color: 'white' }}>
        {label}
      </Typography>
    </Button>
  );
}
